import React, { useContext, useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication, LinkWithPhoneNumberOptions, User } from '@capacitor-firebase/authentication';

import {
  signOut,
  RecaptchaVerifier,
  signInWithCredential,
  EmailAuthProvider,
  linkWithCredential,
  GoogleAuthProvider,
  getAuth,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
} from 'firebase/auth';

import { captureAuthEvent, captureException } from '../analytics/sentry';
import { processEventQueue, recordEvent, removeUser, updateIdentity } from '../services/user.service';
import { getFirebaseAuth, initializeFirebase } from '../services/firebase.service';
import { getViewportType } from '../hooks/useWindowSize';
import { getIsCI } from '../utils';
import { API_URL, USE_EMULATOR } from '../env';
import { useLocale } from './locale.provider';

export const AUTH_ERROR_CODES = {
  GOOGLE_FAIL_EMAIL_PASSWORD: 'GOOGLE_FAIL_EMAIL_PASSWORD',
  EMAIL_PASSWORD_FAIL_GOOGLE: 'EMAIL_PASSWORD_FAIL_GOOGLE',
  PHONE_VERIFICATION_FAILED: 'PHONE_VERIFICATION_FAILED',
  UNHANDLED: 'UNHANDLED',
};

/**
 * Firebase auth codes that mean "the identity you offered already belongs to another account".
 * Firebase picks between them based on which flow hit the collision (social redirect, phone link,
 * email link), but to us they're one expected outcome — a returning user signing up again — and
 * each one has a dedicated UI path (the "an account exists" modal). They're user error, not
 * defects, so they get a breadcrumb instead of a Sentry issue.
 */
const ACCOUNT_COLLISION_ERROR_CODES = [
  'auth/account-exists-with-different-credential',
  'auth/credential-already-in-use',
  'auth/email-already-in-use',
];

export const isAccountCollisionError = (err: { code?: string }): boolean =>
  ACCOUNT_COLLISION_ERROR_CODES.includes(err?.code);

/**
 * Every auth code we treat as an expected user outcome rather than a defect: the collisions above,
 * plus the two ways an SMS code fails to check out. A mistyped or stale OTP is answered inline
 * ("Code incorrect… you have N attempt(s) left" / "Your code has expired") and the user simply
 * retries, so there is nothing for anyone to action in Sentry.
 *
 * Anything not on this list still reports — including the codes that look adjacent but aren't
 * user error: `auth/invalid-verification-id` and `auth/missing-verification-code` mean we lost or
 * never stored the verification ID, which is our bug, and `auth/too-many-requests` is worth seeing
 * because it can signal abuse rather than one person fumbling a code.
 */
const EXPECTED_AUTH_ERROR_CODES = [
  ...ACCOUNT_COLLISION_ERROR_CODES,
  'auth/invalid-verification-code',
  'auth/code-expired',
];

/**
 * Report an auth failure to Sentry unless it's one of the expected outcomes above, which is
 * recorded as a breadcrumb instead. `flow` identifies the call site so a breadcrumb stays traceable.
 */
const reportAuthError = (err: any, flow: string, context?: Record<string, unknown>) => {
  if (EXPECTED_AUTH_ERROR_CODES.includes(err?.code)) {
    captureAuthEvent('expected auth outcome (handled, not reported)', { flow, code: err.code, ...context });
    return;
  }

  captureException(err, { flow, ...context });
};

const AuthContext = React.createContext(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { locale } = useLocale();
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestOTP, setRequestOTP] = useState(false);
  const [hasValidCaptcha, setHasValidCaptcha] = useState(false);
  const [invisibleCaptcha, setInvisibleCaptcha] = useState(true);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    try {
      const auth = getAuth();
      auth.languageCode = locale;
    } catch (e) {
      console.debug('AuthProvider: unable to set Firebase auth.languageCode yet', e);
    }
    if (window.recaptchaVerifier && window.recaptchaDomId) {
      const domId = window.recaptchaDomId;
      destroyRecaptcha();
      initializeRecaptcha(domId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const confirmationResultRef = useRef<ConfirmationResult | null>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeFirebase({
          setFirebaseUser,
          setLoading,
          setError,
          processEventQueue,
          setInitialized,
          setInvisibleCaptcha,
          destroyRecaptcha,
          setRequestOTP,
        });

        await handleAuthRedirect();
      } catch (e) {
        console.log('AuthProvider - Error', e, 'Initializing');
        captureException(e);

        setLoading(false);
        setInitialized(true);
      }
    };

    initialize();
  }, []);

  const destroyRecaptcha = () => {
    window.recaptchaVerifier?.clear();
    window.recaptchaVerifier = null;
    window.recaptchaWidgetId = null;
    window.recaptchaDomId = null;
  };

  const initializeRecaptcha = (domId: string, overrideSize?: 'invisible' | 'normal') => {
    if (Capacitor.isNativePlatform()) {
      setHasValidCaptcha(true);
      return;
    }

    if (window.recaptchaVerifier) return;

    if (!document.getElementById(domId)) {
      console.warn('Phone Auth: reCaptcha DOM element not found', domId);
      return;
    }

    if (!window.recaptchaVerifier) {
      const auth = Capacitor.isNativePlatform() ? getFirebaseAuth() : getAuth();
      auth.languageCode = locale;
      console.debug('Phone Auth: Disabling app verification for testing', getIsCI());
      auth.settings.appVerificationDisabledForTesting = getIsCI() || USE_EMULATOR;

      const captchaSize = overrideSize ?? (invisibleCaptcha ? 'invisible' : 'normal');
      console.debug('Phone Auth: Creating new captcha', { size: captchaSize, invisibleCaptcha, domId }, document.getElementById(domId));
      window.recaptchaDomId = domId;
      window.recaptchaVerifier = new RecaptchaVerifier(auth, domId, {
        size: captchaSize,
        callback: () => {
          console.debug('Phone Auth: reCaptcha valid-callback');
          setHasValidCaptcha(true);
        },
        'expired-callback': (err) => {
          console.error('Phone Auth: reCaptcha expired-callback — switching to visible', err);
          setInvisibleCaptcha(false);
          setHasValidCaptcha(false);
          destroyRecaptcha();
          initializeRecaptcha(domId, 'normal');
        },
        'error-callback': (err) => {
          console.error('Phone Auth: reCaptcha error-callback — switching to visible', err);
          setInvisibleCaptcha(false);
          setHasValidCaptcha(false);
          destroyRecaptcha();
          initializeRecaptcha(domId, 'normal');
        },
        appVerificationDisabledForTesting: getIsCI(),
      });

      window.recaptchaVerifier.render()
        .then((widgetId) => {
          window.recaptchaWidgetId = widgetId;
        })
        .catch((err) => {
          console.error('Phone Auth: reCaptcha render failed', err);
          destroyRecaptcha();
        });
    } else if (window.recaptchaWidgetId && window.grecaptcha) {
      console.debug('Phone Auth: Resetting captcha');
      window.grecaptcha.reset(window.recaptchaWidgetId);
    }
  };

  const handleSignUp = async () => {
    if (!firebaseUser) {
      setLoading(true);

      recordEvent({ eventName: 'Sign Up Started', firebaseUser });
      await createAnonAccount();

      setLoading(false);
    }
  };

  const handleAuthRedirect = async () => {
    if (Capacitor.isNativePlatform()) {
      setInitialized(true);

      return;
    }

    try {
      const loadingTimeout = setTimeout(() => {
        setLoading(true);
      }, 500);

      const result = await FirebaseAuthentication.getRedirectResult();

      if (result) {
        if (result.user) {
          setFirebaseUser(result.user);
          setInitialized(true);

          await recordEvent({
            eventName: 'Sign In',
            firebaseUser: result.user,
            props: { type: 'SOCIAL' },
          });

          window.onbeforeunload = null;
        }
      }

      clearTimeout(loadingTimeout);
      setLoading(false);
    } catch (err) {
      // `auth/account-exists-with-different-credential` often arrives with no `_tokenResponse`
      // (Firebase omits it when it never got as far as minting a token), so the error code — not
      // the provider — has to drive the branching, or a handled collision falls through to
      // UNHANDLED and gets reported to Sentry.
      const providerId = err.customData?._tokenResponse?.providerId;

      switch (err.code) {
        case 'auth/email-already-in-use':
        case 'auth/account-exists-with-different-credential':
          reportAuthError(err, 'auth-redirect', { providerId });
          setError(AUTH_ERROR_CODES.GOOGLE_FAIL_EMAIL_PASSWORD);
          break;
        case 'auth/credential-already-in-use': {
          reportAuthError(err, 'auth-redirect', { providerId });

          const credential =
            providerId === GoogleAuthProvider.PROVIDER_ID ? GoogleAuthProvider.credentialFromError(err) : null;

          if (!credential) {
            // No credential to replay (non-Google provider, or Firebase withheld it) — fall back to
            // the "sign in with your other credentials" modal rather than silently doing nothing.
            setError(AUTH_ERROR_CODES.GOOGLE_FAIL_EMAIL_PASSWORD);
            break;
          }

          try {
            await signInWithCredential(getFirebaseAuth(), credential);
            const currUser = await FirebaseAuthentication.getCurrentUser();

            setFirebaseUser(currUser.user);
          } catch (e) {
            captureException(e, { flow: 'auth-redirect-credential-replay', providerId });
            setError(AUTH_ERROR_CODES.UNHANDLED);
          }
          break;
        }
        default:
          captureException(err, { flow: 'auth-redirect', providerId });
          setError(AUTH_ERROR_CODES.UNHANDLED);
      }

      setInitialized(true);
      setLoading(false);
    }
  };

  // `signInAnonymously` mints a brand new uid, so running it while someone is already
  // signed in silently swaps a real borrower for an empty account. Their personId and
  // applicationId stay in app state, so every following /borrowers/:personId request
  // fails its ownership check. This ref serialises the call; the checks below make it
  // a no-op whenever a session already exists.
  const anonAccountInFlight = useRef<Promise<void> | null>(null);

  const createAnonAccount = async () => {
    // onSignUp fires this without awaiting and navigates away immediately, so a second
    // tap can re-enter before `loading` has re-rendered the button as disabled.
    if (anonAccountInFlight.current) return anonAccountInFlight.current;

    const run = (async () => {
      setLoading(true);

      try {
        // Ask Firebase rather than the `firebaseUser` state: that state is still null
        // while initializeFirebase restores the persisted session, so a guard on it lets
        // an already-signed-in borrower straight through during app boot.
        const existing = await FirebaseAuthentication.getCurrentUser();

        if (existing?.user) {
          setFirebaseUser(existing.user);

          return;
        }

        const result = await FirebaseAuthentication.signInAnonymously();

        updateIdentity({
          platform: Capacitor.getPlatform(),
          viewport: getViewportType(),
        });
        setFirebaseUser(result.user);
      } catch (err) {
        captureException(err);

        throw err;
      } finally {
        setLoading(false);
      }
    })();

    anonAccountInFlight.current = run;

    try {
      await run;
    } finally {
      anonAccountInFlight.current = null;
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (firebaseUser) {
        await removeUser(firebaseUser.uid, (await FirebaseAuthentication.getIdToken()).token);
      }
    } catch (err) {
      captureException(err);

      throw err;
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    setRequestOTP(false);

    try {
      await FirebaseAuthentication.signOut();

      await signOut(getFirebaseAuth());
      setLoading(false);
    } catch (err) {
      captureException(err);
      setLoading(false);

      throw err;
    }
  };

  const handleRequestPasswordReset = async (email: string) => {
    await FirebaseAuthentication.sendPasswordResetEmail({ email });
  };

  const handleConfirmPasswordReset = async (oobCode: string, newPassword: string) => {
    await FirebaseAuthentication.confirmPasswordReset({ oobCode, newPassword });
  };

  const handleSignIn = async (email: string, password: string): Promise<void> => {
    setLoading(true);

    try {
      const user = await FirebaseAuthentication.getCurrentUser();

      recordEvent({
        eventName: 'Sign In',
        firebaseUser: user.user,
        props: { type: 'EMAIL' },
      });

      const result = await FirebaseAuthentication.signInWithEmailAndPassword({
        email,
        password,
      });

      setFirebaseUser(result.user);
      setLoading(false);
    } catch (err) {
      setLoading(false);

      throw err;
    }
  };

  const handleApplyActionCode = async (oobCode: string) => FirebaseAuthentication.applyActionCode({ oobCode });

  const handelCheckAuthMethod = async (method: string, account: string) => {
    try {
      const response = await fetch(`${API_URL}/v1/auth/check-account/${method}/${account}`, {
        method: 'GET',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
      });

      switch (response.status) {
        case 200:
          return await response.json();
        default:
          throw new Error((await response.json()).message);
      }
    } catch (err) {
      console.log(`check account error: ${err.message}`);
      throw new Error(err);
    }
  };

  const linkAppleAuth = async () => {
    if (firebaseUser) {
      setLoading(true);

      if (Capacitor.isNativePlatform()) {
        try {
          const result = await FirebaseAuthentication.linkWithApple();

          setFirebaseUser(result.user);
          setLoading(false);
        } catch (err) {
          setLoading(false);

          throw err;
        }
      } else {
        window.onbeforeunload = null;
        await FirebaseAuthentication.linkWithApple({ mode: 'redirect' });
      }
    }
  };

  const linkGoogleAuth = async () => {
    if (firebaseUser) {
      setLoading(true);

      if (Capacitor.isNativePlatform()) {
        try {
          const result = await FirebaseAuthentication.linkWithGoogle();

          setFirebaseUser(result.user);
          setLoading(false);
        } catch (err) {
          setLoading(false);

          throw err;
        }
      } else {
        window.onbeforeunload = null;
        await FirebaseAuthentication.linkWithGoogle({ mode: 'redirect' });
      }
    }
  };

  const linkEmailPassword = async (email: string, password: string): Promise<void> => {
    setLoading(true);

    if (firebaseUser) {
      try {
        const isSignUp = firebaseUser?.isAnonymous;
        let result;

        if (Capacitor.isNativePlatform()) {
          result = await FirebaseAuthentication.linkWithEmailAndPassword({
            email,
            password,
          });
        } else {
          const credential = EmailAuthProvider.credential(email, password);

          if (isSignUp) {
            await linkWithCredential(getFirebaseAuth().currentUser, credential);
          }

          await signInWithCredential(getFirebaseAuth(), credential);
          result = await FirebaseAuthentication.getCurrentUser();
        }

        if (isSignUp) {
          recordEvent({
            eventName: 'Sign Up',
            firebaseUser: result.user,
            props: { type: 'EMAIL' },
          });
        } else {
          recordEvent({
            eventName: 'Sign In',
            firebaseUser: result.user,
            props: { type: 'EMAIL' },
          });
        }

        setFirebaseUser(result.user);
        setLoading(false);
      } catch (err) {
        console.error('Error linking email password', err);
        setLoading(false);

        throw err;
      }
    } else {
      console.error('Missing Firebase User for email sign up');
    }
  };

  const handleGoogleAuthSignIn = async () => {
    setLoading(true);

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await FirebaseAuthentication.signInWithGoogle();

        setFirebaseUser(result.user);
      } catch (err) {
        setLoading(false);

        throw err;
      }
    } else {
      window.onbeforeunload = null;
      await FirebaseAuthentication.signInWithGoogle({ mode: 'redirect' });
    }
  };

  const handleAppleAuthSignIn = async () => {
    setLoading(true);
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await FirebaseAuthentication.signInWithApple();
        setFirebaseUser(result.user);
      } catch (err) {
        setLoading(false);
        throw err;
      }
    } else {
      window.onbeforeunload = null;
      await FirebaseAuthentication.signInWithApple({ mode: 'redirect' });
    }
  };

  const handleVerifyEmail = async () => {
    await FirebaseAuthentication.sendEmailVerification();
  };

  const handleVerifyPhoneNumber = async (phoneNumber: string, resendCode: boolean, isSignUp: boolean) => {
    let formattedPhone = phoneNumber.replace(/[^0-9]/g, '');
    formattedPhone = formattedPhone.length === 10 ? `+1${formattedPhone}` : `+${formattedPhone}`;

    try {
      const options: LinkWithPhoneNumberOptions = {
        phoneNumber: formattedPhone,
        resendCode,
      };

      if (!Capacitor.isNativePlatform()) {
        options.recaptchaVerifier = window.recaptchaVerifier;

        const auth = getAuth();
        let confirmationResult;

        if (!window.recaptchaVerifier) {
          initializeRecaptcha(isSignUp ? 'recaptcha-sign-up' : 'recaptcha-sign-in');
        }

        if (!window.recaptchaVerifier) {
          throw new Error('reCAPTCHA verifier failed to initialize. Please refresh the page and try again.');
        }

        if (isSignUp) {
          confirmationResult = await linkWithPhoneNumber(
            auth.currentUser,
            options.phoneNumber,
            window.recaptchaVerifier,
          );
        } else {
          confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
        }

        confirmationResultRef.current = confirmationResult;
        console.log('Phone Auth: confirmationResult', confirmationResult);
      } else {
        if (isSignUp) {
          console.debug('Phone Auth: Linking', options);
          await FirebaseAuthentication.linkWithPhoneNumber(options);
          console.debug('Phone Auth: Linked');
        } else {
          await FirebaseAuthentication.signInWithPhoneNumber(options);
        }
      }
    } catch (err) {
      if (!Capacitor.isNativePlatform() && err?.message?.includes('reCAPTCHA client element has been removed')) {
        destroyRecaptcha();
        const domId = window.recaptchaDomId || (isSignUp ? 'recaptcha-sign-up' : 'recaptcha-sign-in');

        if (document.getElementById(domId)) {
          // Element is back in the DOM — reinitialize and retry once
          try {
            initializeRecaptcha(domId);
            if (window.recaptchaVerifier) {
              const auth = getAuth();
              let retryResult;
              if (isSignUp) {
                retryResult = await linkWithPhoneNumber(auth.currentUser, formattedPhone, window.recaptchaVerifier);
              } else {
                retryResult = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
              }
              confirmationResultRef.current = retryResult;
              console.log('Phone Auth: reCAPTCHA retry succeeded');
              return;
            }
          } catch (retryErr) {
            console.error('Phone Auth: reCAPTCHA retry failed', retryErr);
            destroyRecaptcha();
          }
        }

        // Element still unavailable — expected during auth loading. Don't alert Sentry.
        console.warn('Phone Auth: reCAPTCHA element removed (auth loading), will reinitialize on next attempt');
        setLoading(false);
        setRequestOTP(false);
        throw err;
      }

      console.error('Phone Auth: Verification Failed', err);
      reportAuthError(err, 'phone-verify', { isSignUp, native: Capacitor.isNativePlatform() });

      setLoading(false);
      setRequestOTP(false);

      if (!Capacitor.isNativePlatform() && window.grecaptcha) {
        window.grecaptcha.reset(window.recaptchaWidgetId);
      }

      throw err;
    }
  };

  const handleConfirmPhoneNumber = async (verificationCode: string) => {
    setLoading(true);

    try {
      console.debug('Phone Auth: Confirming With Code', verificationCode, window.verificationId);
      let result;

      if (Capacitor.isNativePlatform()) {
        if (!window.verificationId) {
          throw new Error('No verification ID available. Please request a new code.');
        }

        result = await FirebaseAuthentication.confirmVerificationCode({
          verificationId: window.verificationId,
          verificationCode,
        });
      } else {
        if (!confirmationResultRef.current) {
          throw new Error('No confirmation result available. Please request a new code.');
        }

        result = await confirmationResultRef.current.confirm(verificationCode);
      }

      console.debug('Phone Auth: Confirmation succeeded', result?.user?.uid, 'isAnonymous:', result?.user?.isAnonymous);
      setLoading(false);
      setFirebaseUser(result.user);
    } catch (err) {
      console.error('Phone Auth: Code Error on Confirmation', err, err?.code, err?.message);
      // A phone number already linked to another account throws
      // `auth/account-exists-with-different-credential` here; CreateAccount turns that into the
      // "an account exists with that phone" modal, so it's handled, not a defect.
      reportAuthError(err, 'phone-confirm', { native: Capacitor.isNativePlatform() });

      setLoading(false);

      throw err;
    }
  };

  const context = {
    error,
    setError,
    createAnonAccount,
    handleSignUp,
    handleSignOut,
    handleDeleteUser,
    handleRequestPasswordReset,
    handleConfirmPasswordReset,
    handleSignIn,
    handleApplyActionCode,
    handleAuthRedirect,
    handleGoogleAuthSignIn,
    handleAppleAuthSignIn,
    handleVerifyEmail,
    handleVerifyPhoneNumber,
    handleConfirmPhoneNumber,
    handelCheckAuthMethod,
    linkAppleAuth,
    linkGoogleAuth,
    linkEmailPassword,
    firebaseUser,
    loading,
    initialized,
    initializeRecaptcha,
    destroyRecaptcha,
    hasValidCaptcha,
    requestOTP,
    setRequestOTP,
    setInvisibleCaptcha,
  };

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
};

interface IAuthContext {
  createAnonAccount: () => Promise<void>;
  handleDeleteUser: () => Promise<void>;
  handleSignUp: () => Promise<void>;
  handleSignOut: () => Promise<void>;
  handleRequestPasswordReset: (email: string) => Promise<void>;
  handleConfirmPasswordReset: (code: string, password: string) => Promise<void>;
  handleSignIn: (email: string, password: string) => Promise<void>;
  handleApplyActionCode: (oobCode: string) => Promise<void>;
  linkAppleAuth: () => Promise<User | null>;
  linkGoogleAuth: () => Promise<User | null>;
  handleGoogleAuthSignIn: () => Promise<void>;
  handleAppleAuthSignIn: () => Promise<void>;
  linkEmailPassword: (email: string, password: string) => Promise<User | null>;
  handleVerifyEmail: () => Promise<void>;
  handleVerifyPhoneNumber: (phoneNumber: string, resend: boolean, isSignUp?: boolean) => Promise<void>;
  handleConfirmPhoneNumber: (verificationCode: string, isSignUp?: boolean) => Promise<void>;
  handelCheckAuthMethod: (method: string, account: string) => Promise<any>;
  firebaseUser?: User;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  initializeRecaptcha: (domId: string, overrideSize?: 'invisible' | 'normal') => void;
  destroyRecaptcha: () => void;
  hasValidCaptcha: boolean;
  requestOTP: boolean;
  setRequestOTP: (requestOTP: boolean) => void;
  setInvisibleCaptcha: (invisibleCaptcha: boolean) => void;
}

export const useAuthContext = (): IAuthContext => {
  return useContext(AuthContext);
};
